import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Collapse,
  Divider,
  Alert,
  Skeleton,
  InputAdornment,
  IconButton,
  Tooltip,
  Avatar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import ShieldIcon from "@mui/icons-material/Shield";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import api from "../../utils/api";

/* ── Permission categories (shared with Roles.js) ───────────────────────── */
const PERM_CATEGORIES = [
  { value: "Dashboard",              color: "#2f63ad" },
  { value: "Users & Profiles",       color: "#1a8a5c" },
  { value: "Members",                color: "#546e7a" },
  { value: "Work Orders",            color: "#d4811a" },
  { value: "Violations",             color: "#c0392b" },
  { value: "Architectural Requests", color: "#7b3fa0" },
  { value: "Gate & Security",        color: "#0e7c7b" },
  { value: "Reports",                color: "#8e44ad" },
  { value: "Billing & Integrations", color: "#1565c0" },
  { value: "System Settings",        color: "#607d8b" },
  { value: "Comments",               color: "#795548" },
  { value: "Elections & Voting",     color: "#558b2f" },
  { value: "Marina",                 color: "#00838f" },
  { value: "Notifications",          color: "#f57c00" },
];

const MODULE_TO_CATEGORY = {
  dashboard:      'Dashboard',
  work_orders:    'Work Orders',
  violations:     'Violations',
  architectural:  'Architectural Requests',
  members:        'Members',
  gate:           'Gate & Security',
  billing:        'Billing & Integrations',
  reports:        'Reports',
  elections:      'Elections & Voting',
  marina:         'Marina',
  users:          'Users & Profiles',
  settings:       'System Settings',
  comments:       'Comments',
  notifications:  'Notifications',
};

const resolveCategory = (perm) =>
  MODULE_TO_CATEGORY[perm.module] ||
  "System Settings";

const getCategoryColor = (cat) =>
  PERM_CATEGORIES.find((c) => c.value === cat)?.color || "#607d8b";

/* ── Inline add-override form ───────────────────────────────────────────── */
const AddOverrideForm = ({ onSave, onCancel, saving }) => {
  const [type, setType]     = useState("grant");
  const [reason, setReason] = useState("");

  return (
    <Box
      sx={{
        mt: 1, p: 1.5, borderRadius: "10px",
        backgroundColor: "rgba(47,99,173,0.04)",
        border: "1px solid rgba(47,99,173,0.14)",
      }}
    >
      <RadioGroup
        row
        value={type}
        onChange={(e) => setType(e.target.value)}
        sx={{ mb: 1 }}
      >
        <FormControlLabel
          value="grant"
          control={<Radio size="small" color="success" />}
          label={<Typography variant="body2" sx={{ fontWeight: 600, color: "#2e7d32" }}>Grant</Typography>}
        />
        <FormControlLabel
          value="revoke"
          control={<Radio size="small" color="error" />}
          label={<Typography variant="body2" sx={{ fontWeight: 600, color: "#c62828" }}>Revoke</Typography>}
        />
      </RadioGroup>

      <TextField
        fullWidth
        size="small"
        placeholder="Reason (e.g. Temp access for audit)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        sx={{
          mb: 1,
          "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff" },
        }}
      />

      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <Button
          size="small"
          variant="outlined"
          onClick={onCancel}
          sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={!reason.trim() || saving}
          onClick={() => onSave(type, reason.trim())}
          sx={{
            borderRadius: "8px", textTransform: "none", fontWeight: 700,
            backgroundColor: type === "grant" ? "#2e7d32" : "#c62828",
            "&:hover": { backgroundColor: type === "grant" ? "#1b5e20" : "#b71c1c" },
          }}
        >
          {saving ? "Saving…" : type === "grant" ? "Grant Access" : "Revoke Access"}
        </Button>
      </Box>
    </Box>
  );
};

/* ── Single permission row ───────────────────────────────────────────────── */
const PermRow = ({ perm, override, roleGranted, onSave, onRemove }) => {
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [removing, setRemoving] = useState(false);

  const handleSave = async (type, reason) => {
    setSaving(true);
    try {
      await onSave(perm.PermissionID, type, reason);
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await onRemove(perm.PermissionID);
    } finally {
      setRemoving(false);
    }
  };

  const hasOverride = !!override;
  const isGrant     = override?.type === "grant";

  return (
    <Box
      sx={{
        px: 2, py: 1,
        borderBottom: "1px solid rgba(47,99,173,0.06)",
        borderLeft: hasOverride
          ? `3px solid ${isGrant ? "#2e7d32" : "#c62828"}`
          : "3px solid transparent",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        {/* Permission name */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142" }}>
            {perm.PermissionName || perm.name || `Permission #${perm.PermissionID}`}
          </Typography>
          {perm.description && (
            <Typography variant="caption" sx={{ color: "#6b778c" }}>
              {perm.description}
            </Typography>
          )}
        </Box>

        {/* Role badge */}
        <Tooltip
          title={roleGranted ? "This user's role already grants this permission" : "This permission is not in the user's role(s)"}
          arrow
        >
          <Chip
            size="small"
            icon={roleGranted ? <CheckCircleOutlineIcon sx={{ fontSize: "13px !important" }} /> : <HelpOutlineIcon sx={{ fontSize: "13px !important" }} />}
            label={roleGranted ? "Via role" : "Not in role"}
            sx={{
              height: 20,
              fontSize: "0.7rem",
              fontWeight: 600,
              backgroundColor: roleGranted ? "rgba(46,125,50,0.08)" : "rgba(107,119,140,0.10)",
              color: roleGranted ? "#2e7d32" : "#6b778c",
              "& .MuiChip-icon": { ml: "4px" },
            }}
          />
        </Tooltip>

        {/* Override badge */}
        {hasOverride && (
          <Tooltip title={`Reason: ${override.reason || "No reason provided"}`} arrow>
            <Chip
              size="small"
              icon={isGrant
                ? <CheckCircleOutlineIcon sx={{ fontSize: "13px !important" }} />
                : <BlockIcon sx={{ fontSize: "13px !important" }} />}
              label={isGrant ? "GRANTED" : "REVOKED"}
              sx={{
                height: 22,
                fontSize: "0.72rem",
                fontWeight: 700,
                borderRadius: "6px",
                backgroundColor: isGrant ? "rgba(46,125,50,0.10)" : "rgba(211,47,47,0.10)",
                color: isGrant ? "#2e7d32" : "#c62828",
                border: `1px solid ${isGrant ? "rgba(46,125,50,0.25)" : "rgba(211,47,47,0.25)"}`,
                "& .MuiChip-icon": { ml: "4px" },
              }}
            />
          </Tooltip>
        )}

        {/* Action buttons */}
        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
          {hasOverride ? (
            <>
              <Tooltip title="Change override">
                <IconButton
                  size="small"
                  onClick={() => setShowForm(!showForm)}
                  sx={{ p: 0.5, color: "#2f63ad" }}
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove override (revert to role default)">
                <IconButton
                  size="small"
                  disabled={removing}
                  onClick={handleRemove}
                  sx={{ p: 0.5, color: "#c62828" }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Add permission override for this user">
              <IconButton
                size="small"
                onClick={() => setShowForm(!showForm)}
                sx={{ p: 0.5, color: "#6b778c", "&:hover": { color: "#2f63ad" } }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Inline form */}
      <Collapse in={showForm} timeout="auto" unmountOnExit>
        <AddOverrideForm
          onSave={handleSave}
          onCancel={() => setShowForm(false)}
          saving={saving}
        />
      </Collapse>
    </Box>
  );
};

/* ── Normalize whatever shape the API returns into a plain array ─────────── */
const toArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  // { overrides: [...] } | { data: [...] } | { items: [...] } | { results: [...] }
  const candidate =
    data.overrides ?? data.data ?? data.items ?? data.results ?? null;
  if (Array.isArray(candidate)) return candidate;
  return [];
};

/* ── Main dialog ─────────────────────────────────────────────────────────── */
const UserPermissionOverridesDialog = ({ open, user, onClose }) => {
  const [allPerms, setAllPerms]         = useState([]);
  const [overrides, setOverrides]       = useState([]);
  const [rolePermIds, setRolePermIds]   = useState(new Set());
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");
  const [search, setSearch]             = useState("");
  const [openCats, setOpenCats]         = useState({});
  const [showOnlyOverrides, setShowOnlyOverrides] = useState(false);
  const [snack, setSnack]               = useState({ open: false, msg: "", ok: true });

  const userId = user?.UserID;

  /* ── Load all data on open ────────────────────────────────────────────── */
  const loadData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      // Fetch all permissions + existing overrides in parallel
      const [permsRes, overridesRes] = await Promise.all([
        api.get("/permissions"),
        api.get(`/users/${userId}/permission-overrides`),
      ]);
      setAllPerms(permsRes.data || []);
      setOverrides(toArray(overridesRes.data));

      // Fetch role-based permissions for each of the user's roles
      const roleIds = (user?.Roles || []).map((r) => r.RoleID).filter(Boolean);
      if (roleIds.length) {
        const rolePermsResults = await Promise.all(
          roleIds.map((rid) => api.get(`/role-permissions/${rid}`).catch(() => ({ data: { permissionIds: [] } })))
        );
        const merged = new Set(
          rolePermsResults.flatMap((r) => r.data?.permissionIds || [])
        );
        setRolePermIds(merged);
      }

      // Auto-expand all categories
      const cats = {};
      PERM_CATEGORIES.forEach((c) => (cats[c.value] = true));
      setOpenCats(cats);
    } catch (err) {
      console.error("Failed to load permission data:", err);
      setError("Failed to load permission data. Please close and try again.");
    } finally {
      setLoading(false);
    }
  }, [userId, user?.Roles]);

  useEffect(() => {
    if (open) {
      setSearch("");
      setShowOnlyOverrides(false);
      loadData();
    }
  }, [open, loadData]);

  /* ── Overrides map (permissionId → override) ─────────────────────────── */
  const overridesMap = useMemo(() => {
    const map = {};
    (overrides || []).forEach((o) => {
      const id = o.permissionId ?? o.PermissionID ?? o.permission_id;
      if (id != null) map[id] = o;
    });
    return map;
  }, [overrides]);

  /* ── Grouped + filtered permissions ──────────────────────────────────── */
  const grouped = useMemo(() => {
    const q = search.toLowerCase();
    const map = {};
    allPerms.forEach((p) => {
      const name = (p.PermissionName || p.name || "").toLowerCase();
      if (q && !name.includes(q)) return;
      if (showOnlyOverrides && !overridesMap[p.PermissionID]) return;
      const cat = resolveCategory(p);
      if (!map[cat]) map[cat] = [];
      map[cat].push(p);
    });
    return PERM_CATEGORIES.filter((c) => map[c.value]).map((c) => ({
      ...c,
      perms: map[c.value],
    }));
  }, [allPerms, search, showOnlyOverrides, overridesMap]);

  const overrideCount = overrides.length;
  const grantCount    = overrides.filter((o) => o.type === "grant").length;
  const revokeCount   = overrides.filter((o) => o.type === "revoke").length;

  /* ── Handlers ────────────────────────────────────────────────────────── */
  const handleSave = async (permissionId, type, reason) => {
    try {
      await api.post(`/users/${userId}/permission-overrides`, {
        permissionId,
        type,
        reason,
      });
      // Refresh overrides
      const res = await api.get(`/users/${userId}/permission-overrides`);
      setOverrides(toArray(res.data));
      setSnack({ open: true, msg: `Permission ${type === "grant" ? "granted" : "revoked"} successfully.`, ok: true });
    } catch (err) {
      console.error("Failed to save override:", err);
      setSnack({ open: true, msg: "Failed to save override. Please try again.", ok: false });
      throw err; // re-throw so PermRow can reset its saving state
    }
  };

  const handleRemove = async (permissionId) => {
    try {
      await api.delete(`/users/${userId}/permission-overrides/${permissionId}`);
      setOverrides((prev) =>
        prev.filter((o) => {
          const id = o.permissionId ?? o.PermissionID ?? o.permission_id;
          return id !== permissionId;
        })
      );
      setSnack({ open: true, msg: "Override removed. permission reverts to role default.", ok: true });
    } catch (err) {
      console.error("Failed to remove override:", err);
      setSnack({ open: true, msg: "Failed to remove override. Please try again.", ok: false });
      throw err;
    }
  };

  const fullName = user
    ? `${user.Name || ""} ${user.Lastname || ""}`.trim() || user.Username
    : "";

  /* ── Render ──────────────────────────────────────────────────────────── */
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "20px",
          background: "rgba(255,255,255,0.97)",
          backdropFilter: "blur(8px)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ── Header ────────────────────────────────────────────────────── */}
      <DialogTitle
        sx={{
          px: 3, pt: 3, pb: 0,
          display: "flex", alignItems: "flex-start",
          justifyContent: "space-between", gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            sx={{
              width: 42, height: 42, fontSize: "1rem", fontWeight: 700,
              backgroundColor: "#2f63ad",
              boxShadow: "0 4px 12px rgba(47,99,173,0.28)",
            }}
          >
            {user?.Name?.charAt(0) || user?.Username?.charAt(0) || "U"}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#223142", lineHeight: 1.2 }}>
              Permission Overrides
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              {fullName}
              {user?.Roles?.length
                ? ` · ${user.Roles.map((r) => r.RoleName).join(", ")}`
                : ""}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ mt: 0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2, pb: 0, flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Info banner */}
        <Alert
          severity="info"
          icon={<ShieldIcon fontSize="small" />}
          sx={{ borderRadius: "12px", fontSize: "0.85rem" }}
        >
          Overrides take precedence over the user's role permissions. Use them for temporary access adjustments without changing the role itself.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ borderRadius: "12px" }}>{error}</Alert>
        )}

        {/* Snack inline */}
        {snack.open && (
          <Alert
            severity={snack.ok ? "success" : "error"}
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            sx={{ borderRadius: "12px" }}
          >
            {snack.msg}
          </Alert>
        )}

        {/* Summary chips */}
        {!loading && (
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
            <Typography variant="body2" sx={{ color: "#6b778c", fontWeight: 500 }}>
              Active overrides:
            </Typography>
            {overrideCount === 0 ? (
              <Typography variant="body2" sx={{ color: "#9aa5b4" }}>None</Typography>
            ) : (
              <>
                {grantCount > 0 && (
                  <Chip
                    size="small"
                    icon={<CheckCircleOutlineIcon sx={{ fontSize: "13px !important" }} />}
                    label={`${grantCount} granted`}
                    sx={{
                      height: 22, fontSize: "0.75rem", fontWeight: 700,
                      backgroundColor: "rgba(46,125,50,0.10)", color: "#2e7d32",
                      border: "1px solid rgba(46,125,50,0.25)",
                      "& .MuiChip-icon": { ml: "4px" },
                    }}
                  />
                )}
                {revokeCount > 0 && (
                  <Chip
                    size="small"
                    icon={<BlockIcon sx={{ fontSize: "13px !important" }} />}
                    label={`${revokeCount} revoked`}
                    sx={{
                      height: 22, fontSize: "0.75rem", fontWeight: 700,
                      backgroundColor: "rgba(211,47,47,0.10)", color: "#c62828",
                      border: "1px solid rgba(211,47,47,0.25)",
                      "& .MuiChip-icon": { ml: "4px" },
                    }}
                  />
                )}
              </>
            )}

            {overrideCount > 0 && (
              <Button
                size="small"
                variant={showOnlyOverrides ? "contained" : "outlined"}
                onClick={() => setShowOnlyOverrides((v) => !v)}
                sx={{
                  ml: "auto",
                  borderRadius: "8px", textTransform: "none", fontWeight: 600,
                  fontSize: "0.78rem", py: 0.25, px: 1.25,
                  borderColor: "rgba(47,99,173,0.25)",
                  ...(showOnlyOverrides
                    ? { backgroundColor: "#2f63ad", color: "#fff" }
                    : { color: "#2f63ad" }),
                }}
              >
                {showOnlyOverrides ? "Show all" : "Show overrides only"}
              </Button>
            )}
          </Box>
        )}

        {/* Search */}
        <TextField
          size="small"
          fullWidth
          placeholder="Search permissions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#9aa5b4" }} />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        />

        {/* Permission list */}
        <Box sx={{ flex: 1, overflowY: "auto", borderRadius: "12px", border: "1px solid rgba(47,99,173,0.08)" }}>
          {loading ? (
            <Box sx={{ p: 2 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Box key={i} sx={{ mb: 1.5 }}>
                  <Skeleton variant="text" width="30%" height={20} sx={{ mb: 0.5 }} />
                  {Array.from({ length: 3 }).map((__, j) => (
                    <Skeleton key={j} variant="rounded" height={44} sx={{ mb: 0.5, borderRadius: "8px" }} />
                  ))}
                </Box>
              ))}
            </Box>
          ) : grouped.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "#9aa5b4" }}>
                {search
                  ? "No permissions match your search."
                  : showOnlyOverrides
                  ? "No overrides have been set for this user."
                  : "No permissions found."}
              </Typography>
            </Box>
          ) : (
            grouped.map(({ value: cat, color, perms }) => (
              <Box key={cat}>
                {/* Category header */}
                <Box
                  onClick={() => setOpenCats((prev) => ({ ...prev, [cat]: !prev[cat] }))}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    px: 2, py: 1,
                    cursor: "pointer",
                    userSelect: "none",
                    backgroundColor: "rgba(47,99,173,0.03)",
                    borderBottom: "1px solid rgba(47,99,173,0.08)",
                    position: "sticky", top: 0, zIndex: 1,
                    "&:hover": { backgroundColor: "rgba(47,99,173,0.06)" },
                  }}
                >
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#223142", flex: 1, letterSpacing: 0.3 }}>
                    {cat.toUpperCase()}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#9aa5b4", mr: 0.5 }}>
                    {perms.length} permission{perms.length !== 1 ? "s" : ""}
                    {perms.filter((p) => overridesMap[p.PermissionID]).length > 0 &&
                      ` · ${perms.filter((p) => overridesMap[p.PermissionID]).length} overridden`}
                  </Typography>
                  {openCats[cat]
                    ? <ExpandLessIcon sx={{ fontSize: 16, color: "#9aa5b4" }} />
                    : <ExpandMoreIcon sx={{ fontSize: 16, color: "#9aa5b4" }} />}
                </Box>

                {/* Permissions in this category */}
                <Collapse in={openCats[cat] !== false} timeout="auto">
                  {perms.map((perm) => (
                    <PermRow
                      key={perm.PermissionID}
                      perm={perm}
                      override={overridesMap[perm.PermissionID] || null}
                      roleGranted={rolePermIds.has(perm.PermissionID)}
                      onSave={handleSave}
                      onRemove={handleRemove}
                    />
                  ))}
                </Collapse>
                <Divider />
              </Box>
            ))
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid rgba(47,99,173,0.08)" }}>
        <Typography variant="caption" sx={{ color: "#9aa5b4", flex: 1 }}>
          Changes are saved immediately per permission.
        </Typography>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            borderRadius: "10px", textTransform: "none", fontWeight: 700,
            backgroundColor: "#2f63ad", boxShadow: "none",
            "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserPermissionOverridesDialog;
